import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AgentsAiProvider } from '@trovata/app/features/ai-agents/services/agents-ai-provider';
import { AgentsWebSocketService } from '@trovata/app/features/ai-agents/services/websocket/agents-websocket.service';
import { AgentStreamEvent } from '@trovata/app/features/ai-agents/models/agents-websocket.model';

/**
 * CashAgentsAiProvider — the Cash implementation of {@link AgentsAiProvider}.
 *
 * A pure pass-through to `AgentsWebSocketService` (Cash configurator/preview WS
 * routes `ai.agent.chat.configuration.v1` / `ai.agent.chat.preview.v1`). Each
 * method delegates to the same `AgentsWebSocketService` method with the same
 * arguments, so the Cash builder path resolves identically to the pre-extraction
 * code. This is the Cash-isolation guarantee: extracting the edit-AI seam adds
 * only a transparent layer of indirection.
 *
 * `AgentsWebSocketService` itself is untouched (and `sendUnifiedChat` still calls
 * it directly from the state — only the builder's chat/preview/abort route here).
 */
@Injectable({
	providedIn: 'root',
})
export class CashAgentsAiProvider implements AgentsAiProvider {
	private readonly agentsWsService: AgentsWebSocketService = inject(AgentsWebSocketService);

	sendConfigurationChat(prompt: string, sessionId?: string): Observable<AgentStreamEvent> {
		return this.agentsWsService.sendConfigurationChat(prompt, sessionId);
	}

	sendPreviewChat(prompt: string, timezone?: string): Observable<AgentStreamEvent> {
		return this.agentsWsService.sendPreviewChat(prompt, timezone);
	}

	abortChatStream(): void {
		this.agentsWsService.abortChatStream();
	}

	abortPreviewStream(): void {
		this.agentsWsService.abortPreviewStream();
	}

	abortStream(): void {
		this.agentsWsService.abortStream();
	}
}
